import { normalizeStructuredLocation, type SearchFiltersDraft } from "@/lib/search-filter-model"

export interface SearchPreflightSentencePart {
  text: string
  tone: "body" | "highlight"
}

function formatCompactItem(primary: string, count: number) {
  return count > 1 ? `${primary} +${count - 1}` : primary
}

function formatOrList(items: string[]) {
  const filteredItems = items.map((item) => item.trim()).filter(Boolean)

  if (filteredItems.length === 0) return ""
  if (filteredItems.length === 1) return filteredItems[0]

  return filteredItems.join(" or ")
}

function formatListWithOverflow(items: string[], visibleCount = 2) {
  const filteredItems = items.map((item) => item.trim()).filter(Boolean)

  if (filteredItems.length === 0) return ""
  if (filteredItems.length <= visibleCount) {
    if (filteredItems.length === 1) return filteredItems[0]
    return `${filteredItems.slice(0, -1).join(", ")}, or ${filteredItems[filteredItems.length - 1]}`
  }

  const visible = filteredItems.slice(0, visibleCount).join(", ")
  const remaining = filteredItems.length - visibleCount
  return `${visible}, or ${remaining} other ${remaining === 1 ? "company" : "companies"}`
}

function getLocationLabel(filters: SearchFiltersDraft) {
  const structuredLocations = filters.locationFull
    .map((location) => normalizeStructuredLocation(location))
    .map((location) => location.label || [location.city, location.region].filter(Boolean).join(", "))
    .filter(Boolean)

  if (structuredLocations.length > 0) {
    return formatOrList(structuredLocations)
  }

  if (filters.locations.length > 0) return formatOrList(filters.locations)
  if (filters.locationRegion.length > 0) return formatOrList(filters.locationRegion)
  if (filters.locationCountry === "CA") return "Canada"
  if (filters.locationCountry === "US") return "United States"

  return ""
}

function getExperienceLabel(filters: SearchFiltersDraft) {
  if (filters.minExperience && filters.maxExperience) {
    return `${filters.minExperience}-${filters.maxExperience} years`
  }

  if (filters.minExperience) {
    return `${filters.minExperience}+ years`
  }

  if (filters.maxExperience) {
    return `up to ${filters.maxExperience} years`
  }

  return ""
}

/**
 * Builds the conversational assistant sentence shown in the search preflight UI.
 * This keeps wording and highlight segmentation deterministic so the renderer stays
 * small and the summary can be covered with unit tests.
 */
export function buildSearchPreflightSummary(filters: SearchFiltersDraft): SearchPreflightSentencePart[] {
  const parts: SearchPreflightSentencePart[] = []
  const titleLabel = filters.jobTitles.length > 0 ? formatCompactItem(filters.jobTitles[0], filters.jobTitles.length) : ""
  const locationLabel = getLocationLabel(filters)
  const experienceLabel = getExperienceLabel(filters)
  const companyLabel = formatListWithOverflow(filters.companies)
  const skillsLabel = formatListWithOverflow(filters.skills, 3)
  const keywordsLabel = formatListWithOverflow(filters.keywords, 3)

  const pushText = (text: string) => {
    if (text) {
      parts.push({ text, tone: "body" })
    }
  }

  const pushHighlight = (text: string) => {
    if (text) {
      parts.push({ text, tone: "highlight" })
    }
  }

  if (titleLabel) {
    pushHighlight(titleLabel)
  } else {
    pushText("People")
  }

  if (locationLabel) {
    pushText(" in ")
    pushHighlight(locationLabel)
  }

  if (experienceLabel) {
    pushText(" with ")
    pushHighlight(experienceLabel)
    pushText(" of experience")
  }

  if (companyLabel) {
    pushText(filters.currentOnly === "current-only" ? " who currently works at " : " who currently works or used to work at ")
    pushHighlight(companyLabel)
  }

  if (skillsLabel) {
    pushText(" with skills in ")
    pushHighlight(skillsLabel)
  }

  if (keywordsLabel) {
    pushText(" and profiles mentioning ")
    pushHighlight(keywordsLabel)
  }

  if (parts.length === 0) {
    return [
      {
        text: "I picked up a search intent from your prompt. Open the filters to review and refine what will run.",
        tone: "body",
      },
    ]
  }

  pushText(".")
  return parts
}
